<template>
  <div>
    <div class="flex flex-col items-center gap-4 w-full" v-if="layers.length > 0">
      <div v-if="showTitle" class="flex items-center justify-between w-full max-w-xl">
        <h2 class="font-medium">Capas existentes a configurar en los pictogramas</h2>
        <b-button
          v-if="canConfigureLayer && layers.length > 1"
          type="is-primary"
          size="is-small"
          icon-pack="fas"
          icon-left="layer-group"
          @click="_HandleClickedConfigAllLayers"
        >
          {{ $t('configure_all_layers') }}
        </b-button>
      </div>

      <div v-for="(layer, index) of layers"
        class="flex flex-col w-full max-w-xl bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
      >
        <!-- PANEL HEADER -->
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100"
          @click="_toggleLayerExpansion(layer)"
        >
          <div class="flex items-center space-x-2 text-gray-800 font-medium">
            <b-icon pack="fas" :icon="_GetIcon(layer)" class="text-blue-light" />
            <span>{{ layer.name }} ({{ layer.weight }}%)</span>
          </div>

          <b-tooltip v-if="canEditLayer"
            :label="'Proximamente se podrá editar el capa'"
            position="is-left"
          >
            <div
              class="flex items-center justify-center w-8 h-8 rounded-md bg-blue-lighter hover:bg-blue cursor-pointer"
              @click.stop="_HandleClickedEditLayer(layer)"
            >
              <b-icon pack="fas" icon="edit" class="text-white text-xs" />
            </div>
          </b-tooltip>
        </div>

        <!-- PANEL BODY -->
        <div v-show="layer.expanded"
          class="px-4 py-4 space-y-6 text-sm text-gray-700 transition-all duration-300 ease-in-out">
          <!-- CONFIGURACION DE ESTADOS -->
          <LayerStatusesConfig
            :data="layer.config.statuses"
            :code-layer="layer.code"
            :can-edit-config="canConfigureLayer"
            @epc-open-config-modal="_HandleClickedConfigLayer"
          />

          <!-- ESTADOS -->
          <section v-if="layer.config.statuses && layer.config.statuses.avance_method">
            <h3 class="text-gray-600 font-semibold mb-4">Estados</h3>

            <group-statuses v-if="_getAvanceMethod(layer) === 'grouped'"
              :ref="'refGroupStatuses' + layer.code"
              :groups="layer.statuses"
              :ungrouped-states="layer.ungrouped"
              :code-layer="layer.code"
              :external-control="true"
              :weighting-criteria="_getWeightingCriteria(layer)"
              @epc-group-statuses-delete="(data) => { _deleteLayerStatus(data.group, data.codeLayer) }"
              @epc-group-statuses-updated="(data) => {  _updateLayerStatuses(data.group, data.codeLayer) }"
              @epc-group-ungrouped-statuses-updated="(data) => { _updateLayerUngroupedStates(data.status, data.codeLayer) }"
            />

            <statuses v-else-if="_getAvanceMethod(layer) === 'default'"
              :ref="'refStatuses' + layer.code"
              :statuses="layer.statuses"
              :code-layer="layer.code"
              :weighting-criteria="_getWeightingCriteria(layer)"
            />
          </section>
        </div>
      </div>

      <div v-if="canSaveStatuses">
        <v-button-processing v-if="processingData" />
        <b-button v-else
          :label="$t('save')"
          type="is-success"
          icon-pack="fas"
          icon-left="save"
          expanded
          @click="_OnSave" />
      </div>

      <layer-statuses-config-modal
        :ref="'refLayerStatusesConfigModal'"
        :text-action="$t('configure')"
        :icon-name="'cog'"
        @epc-perform-action="_updateLayerStatusesConfig"
      />
    </div>
    <empty-panel v-else />
  </div>
</template>

<script>
import LayerStatusesConfig from '../layer-statuses-config/layer-statuses-config.vue'
import LayerStatusesConfigModal from '../layer-statuses-config/layer-statuses-config-modal.vue'

import Statuses from '../statuses/statuses.vue'
import GroupStatuses from '../group-statuses/group-statuses.vue'
import EmptyPanel from '../empty-panel.vue';

import { mapLayersLegacy as mapLayers } from "../../services/layers.service.js";
// export const UpdateLayerStatusesKey = Symbol('UpdateLayerStatuses');

export default {
  name: 'LayerStatusesList',

  provide() {
    return {
      // [UpdateLayerStatusesKey]: this._updateLayerStatuses,
      'UpdateLayerStatuses': this._updateLayerStatuses,
      'DeleteLayerStatus': this._deleteLayerStatus,
    };
  },

  components: {
    LayerStatusesConfig,
    LayerStatusesConfigModal,
    Statuses,
    GroupStatuses,
    EmptyPanel,
  },

  props: {
    layers: {
      type: Array,
      required: true
    },
    showTitle: {
      type: Boolean,
      default: true
    },
    canEditLayer: {
      type: Boolean,
      default: true
    },
    canConfigureLayer: {
      type: Boolean,
      default: true
    },
    canSaveStatuses: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      processingData: false
    };
  },

  methods: {
    _toggleLayerExpansion(layer) {
      this.layers.forEach(l => {
        if (l !== layer) l.expanded = false;
      });
      layer.expanded = !layer.expanded;
    },

    _getAvanceMethod(lyr) {
      if (!lyr || !lyr.config || !lyr.config.statuses) {
        return null;
      }

      // si el layer tiene configurados los estados, devolvemos el método de avance
      // si no, devolvemos null
      return lyr.config.statuses.avance_method != null ? lyr.config.statuses.avance_method : null;
    },

    _getWeightingCriteria(lyr) {
      if (!lyr || !lyr.config || !lyr.config.statuses) {
        return null;
      }

      // si el layer tiene configurados los estados, devolvemos el criterio de ponderación
      // si no, devolvemos null
      return lyr.config.statuses.weighting_criteria != null ? lyr.config.statuses.weighting_criteria : null;
    },

    _GetIcon(layer) {
      return layer.icon ?? 'layer-group'
    },

    _HandleClickedConfigLayer(data) {
      this.$refs.refLayerStatusesConfigModal.show(data)
    },

    _HandleClickedEditLayer(lyr) {
      // TODO: implementar la lógica para editar la capa proximamente
    },

    _updateLayerUngroupedStates(status, codeLayer) {
      // localizamos el layer que se va a actualizar
      const indexLyr = this.layers.findIndex(l => l.code === codeLayer);
      // si no existe el layer, no hacemos nada
      if (indexLyr === -1) { return; }

      // eliminamos el estado existente
      const indexStatus = this.layers[indexLyr].ungrouped.findIndex(s => s.id === status.id);
      if (indexStatus !== -1) {
        this.layers[indexLyr].ungrouped.splice(indexStatus, 1);
      }
    },

    _updateLayerStatuses(status, codeLayer) {
      // localizamos el layer que se va a actualizar
      const indexLyr = this.layers.findIndex(l => l.code === codeLayer);
      // si no existe el layer, no hacemos nada
      if (indexLyr === -1) { return; }
      if (status.group_id !== null && status.group_id !== undefined) {
        // si el estado tiene un grupo, lo agregamos al grupo correspondiente
        this._updateLayerGroupStatus(status, indexLyr);
      } else {
        // si no tiene grupo, lo agregamos directamente a los estados del layer
        this._updateLayerStatus(status, indexLyr);
      }
    },

    _deleteLayerStatus(status, codeLayer) {
      // localizamos el layer que se va a actualizar
      const indexLyr = this.layers.findIndex(l => l.code === codeLayer);

      if (status.group_id !== null && status.group_id !== undefined) {
        // si el estado tiene un grupo, lo eliminamos del grupo correspondiente
        this._deleteLayerGroupStatus(status, indexLyr);
      } else {
        // si no tiene grupo, lo eliminamos directamente de los estados del layer
        this._deleteLayerStatusDirect(status, indexLyr);
      }
    },

    _updateLayerGroupStatus(status, indexLyr) {
      // actualizamos el estado existente
      const indexGroup = this.layers[indexLyr].statuses.findIndex(s => s.id === status.group_id);
      if (indexGroup === -1) { return ; }

      const group = this.layers[indexLyr].statuses[indexGroup];
      // console.log('LayerStatusesList._updateLayerGroupStatus', status, indexLyr, indexGroup);
      const indexStatus = group.statuses.findIndex(s => s.id === status.id);
      if (indexStatus !== -1) {
        // group.statuses[indexStatus] = status;
        this.$set(group.statuses, indexStatus, status);

      } else {
        // si no existe, lo agregamos
        group.statuses.push(status);
      }
    },

    _updateLayerStatus(status, indexLyr) {
      // actualizamos el estado existente
      const layer = this.layers[indexLyr];

      if (!Array.isArray(layer.statuses)) {
        this.$set(layer, 'statuses', []);
      }

      const indexStatus = layer.statuses.findIndex(s => s.id === status.id);
      if (indexStatus !== -1) {
        this.$set(layer.statuses, indexStatus, status);
      } else {
        // si no existe, lo agregamos
        layer.statuses.push(status);
      }
    },

    _deleteLayerGroupStatus(status, indexLyr) {
      // actualizamos el estado existente
      const indexGroup = this.layers[indexLyr].statuses.findIndex(s => s.id === status.group_id);
      if (indexGroup === -1) { return ; }

      const group = this.layers[indexLyr].statuses[indexGroup];
      // console.log('LayerStatusesList._updateLayerGroupStatus', status, indexLyr, indexGroup);

      // eliminamos el estado existente
      if (status.id > 0) {
        this.layers[indexLyr].ungrouped.push(status);
      }

      const indexStatus = group.statuses.findIndex(s => s.id === status.id);
      if (indexStatus !== -1) {
        group.statuses.splice(indexStatus, 1);
      }
    },

    _deleteLayerStatusDirect(status, indexLyr) {
      // si no existe el layer, no hacemos nada
      if (indexLyr === -1) { return; }

      // eliminamos el estado existente
      if (status.id > 0) {
        this.$notify.error("No puede eliminar el estado puede tener registro de avance.");
        return
      }

      const indexStatus = this.layers[indexLyr].statuses.findIndex(s => s.id === status.id);
      if (indexStatus !== -1) {
        this.layers[indexLyr].statuses.splice(indexStatus, 1);
      }
    },

    _updateLayerStatusesConfig(data) {
      // Modo masivo: el modal se abrió desde "Configurar todos los layers"
      // (codeLayer es null). Aplicamos la config a todas las capas tras
      // confirmar — sobrescribir N configs requiere consentimiento explícito.
      if (data.codeLayer === null) {
        this._ApplyConfigToAllLayers(data.config);
        return;
      }
      // Modo individual: actualizamos solo la capa identificada por codeLayer.
      const indexLyr = this.layers.findIndex(l => l.code === data.codeLayer);
      if (indexLyr === -1) { return; }
      const layer = this.layers[indexLyr];
      this.$set(layer.config, 'statuses', data.config);
    },

    _HandleClickedConfigAllLayers() {
      // Reusamos el mismo modal pasando codeLayer=null. Config arranca null
      // para que el usuario decida desde cero — más predecible que sugerir
      // valores de algún layer en particular.
      this.$refs.refLayerStatusesConfigModal.show({ codeLayer: null, config: null });
    },

    _ApplyConfigToAllLayers(config) {
      this.$buefy.dialog.confirm({
        title:       this.$t('bulk_config_title'),
        message:     this.$t('bulk_config_warning', { n: this.layers.length }),
        confirmText: this.$t('apply_to_all'),
        cancelText:  this.$t('cancel'),
        type:        'is-warning',
        hasIcon:     true,
        onConfirm: () => {
          // Deep clone para que cada layer tenga su propia instancia
          // de config.statuses — sin esto, editar una capa después
          // afectaría a todas las demás vía referencia compartida.
          this.layers.forEach((layer) => {
            this.$set(layer.config, 'statuses', JSON.parse(JSON.stringify(config)));
          });
          this.$toast.open({
            message:  this.$t('bulk_config_applied'),
            type:     'is-success',
            position: 'is-top-right',
          });
        },
      });
    },

    _OnSave() {
      const layers = this.layers.map(l => ({
          id: l.id,
          code_layer: l.code,
          config: l.config,
          statuses: l.statuses
        }))

      this.$emit('epc-save-layers-statuses', layers);
    },
  }
}
</script>
